import { injectable } from 'inversify';
import { observable, action, runInAction } from 'mobx';
import { FormsApiService, InvoicesApiService } from 'src/api';
import {
    isInvoiceSignature,
    isFormSignature,
    isProjectSignature,
} from 'src/signature/util/document-utils';
import { SignatureStore, SignatureDocument, SignDocumentRequest } from '../stores/signature.store';

interface SignatureOptions {
    signatureBase64?: string;
    signatureText?: string;
}

@injectable()
export class SignModalStore {
    @observable isLoading: boolean = false;
    @observable isPdfLoading: boolean = true;
    @observable isSubmitSuccess: boolean = false;
    private visitActionId?: string;

    constructor(
        private formApi: FormsApiService,
        private signatureStore: SignatureStore,
        private invoiceApi: InvoicesApiService
    ) {}

    @action
    setIsLoading = (isLoading: boolean) => {
        this.isLoading = isLoading;
    };

    @action
    setIsSubmitSuccess = (isSubmitSuccess: boolean) => {
        this.isSubmitSuccess = isSubmitSuccess;
    };

    @action
    showPdfLoader = () => {
        this.isPdfLoading = true;
    };

    @action
    hidePdfLoader = () => {
        this.isPdfLoading = false;
    };

    @action
    sign = async (request: SignDocumentRequest, signingMethod: Function) => {
        this.isLoading = true;

        try {
            await signingMethod(this.visitActionId, request);
        } catch (e) {
            throw e;
        }

        runInAction(() => {
            this.isLoading = false;
            this.isSubmitSuccess = true;
        });
    };

    setVisitActionId(visitActionId: string) {
        this.visitActionId = visitActionId;
    }

    prepDocument = (document: SignatureDocument) => {
        let signatureRequest;
        let signingMethod = null;

        if (isFormSignature(document)) {
            signatureRequest = {
                submittingId: document.submittingId,
            };
            // Todo: Update swagger to use Public Class Fields to aviod this
            signingMethod = this.formApi.sign.bind(this.formApi);
        }

        if (isInvoiceSignature(document)) {
            const { signatureType, invoiceId } = document;
            signatureRequest = { signatureType, invoiceId };
            signingMethod = this.invoiceApi.signInvoice.bind(this.invoiceApi);
        }

        if (isProjectSignature(document)) {
            const { signatureType, jobId, projectId } = document;
            signatureRequest = { signatureType, jobId, projectId };
            signingMethod = this.invoiceApi.signProject.bind(this.invoiceApi);
        }

        return { signatureRequest, signingMethod };
    };

    signDocument = async (document: SignatureDocument, options: SignatureOptions) => {
        const { signatureRequest, signingMethod } = this.prepDocument(document);

        if (signatureRequest && signingMethod) {
            const docSignatureRequest = <SignDocumentRequest>{
                ...signatureRequest,
                ...options,
            };

            try {
                await this.sign(docSignatureRequest, signingMethod);
            } catch {
                this.isLoading = false;
                return;
            }

            await this.signatureStore.loadDocuments();
        }
    };
}
